import { geometryBounds } from "@/src/core/geometry/boolean";
import { t } from "@/src/i18n";
import { getWallObject } from "@/src/core/engine/wallObjects";
import { getFloorObject } from "@/src/core/engine/floorObjects";
import { getMarkerObject } from "@/src/core/engine/markerObjects";
import { getTerrainPatch } from "@/src/core/engine/terrainPatches";
import { floorForMode, hexesForMode, objectsForMode } from "@/src/core/mapMode";
import {
  axialToWorld,
  hexCorners,
  hexRadius,
  terrainBounds,
  visibleHexes,
} from "@/src/core/engine/HexGrid";
import type {
  HexCell,
  MapMeta,
  MapObject,
  MultiPolygon,
  Point,
  Polygon,
  TerrainPatch,
  TerrainId,
  Viewport,
} from "@/src/types/map";

/**
 * Corner sign pairs in (x, y) order, typed as Point so destructuring yields
 * numbers rather than `number | undefined`. A bare `[[-1, -1], ...]` literal
 * widens to number[][], which is what hid these reads from the type checker.
 */
const CORNER_SIGNS: readonly Point[] = [[-1, -1], [1, -1], [-1, 1], [1, 1]];

/** Table-leg positions as a fraction of the stamp's width/height. */
const TABLE_LEG_OFFSETS: readonly Point[] = [
  [-0.34, -0.28], [0.34, -0.28], [-0.34, 0.28], [0.34, 0.28],
];

interface RenderOptions {
  context: CanvasRenderingContext2D;
  width: number;
  height: number;
  deviceScale: number;
  viewport: Viewport;
  meta: MapMeta;
  geometry: MultiPolygon;
  objects?: MapObject[];
  hexCells?: HexCell[];
  terrainPatches?: TerrainPatch[];
  selectedObjectId?: string | null;
  preview?: { polygon: Polygon; mode: "add" | "subtract" } | null;
  draftPolygon?: { points: Point[]; cursor: Point | null } | null;
  measurement?: { start: Point; end: Point; label: string } | null;
  hexBrushPreview?: { q: number; r: number; mode: "paint" | "erase" } | null;
  showVignette?: boolean;
}

const polygonPath = (polygon: Polygon): Path2D => {
  const path = new Path2D();
  polygon.forEach((ring) => {
    ring.forEach(([x, y], index) => {
      if (index === 0) path.moveTo(x, y);
      else path.lineTo(x, y);
    });
    path.closePath();
  });
  return path;
};

const geometryPath = (geometry: MultiPolygon): Path2D => {
  const path = new Path2D();
  geometry.forEach((polygon) => path.addPath(polygonPath(polygon)));
  return path;
};

const makeHatchPattern = (
  context: CanvasRenderingContext2D,
  color: string,
): CanvasPattern | string => {
  const tile = document.createElement("canvas");
  tile.width = 14;
  tile.height = 14;
  const tileContext = tile.getContext("2d");
  if (!tileContext) return color;
  tileContext.strokeStyle = color;
  tileContext.globalAlpha = 0.64;
  tileContext.lineWidth = 1.2;
  tileContext.beginPath();
  tileContext.moveTo(-2, 12);
  tileContext.lineTo(12, -2);
  tileContext.moveTo(2, 16);
  tileContext.lineTo(16, 2);
  tileContext.stroke();
  return context.createPattern(tile, "repeat") ?? color;
};

const drawSquareGrid = (
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  viewport: Viewport,
  gridSize: number,
) => {
  const left = -viewport.x / viewport.zoom;
  const top = -viewport.y / viewport.zoom;
  const right = left + width / viewport.zoom;
  const bottom = top + height / viewport.zoom;
  const startX = Math.floor(left / gridSize) * gridSize;
  const startY = Math.floor(top / gridSize) * gridSize;
  const minor = new Path2D();
  const major = new Path2D();

  for (let x = startX; x <= right + gridSize; x += gridSize) {
    const target = Math.round(x / gridSize) % 5 === 0 ? major : minor;
    target.moveTo(x, top - gridSize);
    target.lineTo(x, bottom + gridSize);
  }
  for (let y = startY; y <= bottom + gridSize; y += gridSize) {
    const target = Math.round(y / gridSize) % 5 === 0 ? major : minor;
    target.moveTo(left - gridSize, y);
    target.lineTo(right + gridSize, y);
  }

  context.lineWidth = 1 / viewport.zoom;
  context.strokeStyle = "rgba(67, 54, 43, 0.13)";
  context.stroke(minor);
  context.lineWidth = 1.35 / viewport.zoom;
  context.strokeStyle = "rgba(67, 54, 43, 0.22)";
  context.stroke(major);
};

const terrainColors: Record<TerrainId, { fill: string; ink: string }> = {
  plains: { fill: "#b8c991", ink: "#52663a" },
  forest: { fill: "#789267", ink: "#263f2c" },
  hills: { fill: "#c9a86b", ink: "#76572f" },
  mountain: { fill: "#98968d", ink: "#484943" },
  water: { fill: "#74a9bf", ink: "#285b70" },
  swamp: { fill: "#76866b", ink: "#344933" },
  desert: { fill: "#dfc27b", ink: "#8a672d" },
  snow: { fill: "#dce7e7", ink: "#6e858e" },
  settlement: { fill: "#c89c78", ink: "#673c2b" },
};

const hexPath = (cell: { q: number; r: number }, gridSize: number) => {
  const path = new Path2D();
  hexCorners(cell, gridSize).forEach(([x, y], index) => {
    if (index === 0) path.moveTo(x, y);
    else path.lineTo(x, y);
  });
  path.closePath();
  return path;
};

const drawTerrainSymbol = (
  context: CanvasRenderingContext2D,
  cell: HexCell,
  gridSize: number,
  viewport: Viewport,
) => {
  const [x, y] = axialToWorld(cell, gridSize);
  const radius = hexRadius(gridSize);
  const ink = terrainColors[cell.terrain].ink;
  context.save();
  context.translate(x, y);
  context.strokeStyle = ink;
  context.fillStyle = ink;
  context.lineWidth = 1.3 / viewport.zoom;
  context.lineCap = "round";
  context.lineJoin = "round";

  if (cell.terrain === "forest") {
    [-0.24, 0, 0.24].forEach((offset, index) => {
      const treeX = offset * gridSize;
      const treeY = index === 1 ? -radius * 0.12 : radius * 0.1;
      context.beginPath();
      context.moveTo(treeX, treeY - radius * 0.34);
      context.lineTo(treeX - radius * 0.2, treeY + radius * 0.14);
      context.lineTo(treeX + radius * 0.2, treeY + radius * 0.14);
      context.closePath();
      context.fill();
      context.fillRect(treeX - radius * 0.025, treeY + radius * 0.12, radius * 0.05, radius * 0.2);
    });
  } else if (cell.terrain === "mountain") {
    context.beginPath();
    context.moveTo(-radius * 0.55, radius * 0.38);
    context.lineTo(-radius * 0.12, -radius * 0.42);
    context.lineTo(radius * 0.18, radius * 0.1);
    context.lineTo(radius * 0.38, -radius * 0.24);
    context.lineTo(radius * 0.65, radius * 0.38);
    context.closePath();
    context.stroke();
    context.beginPath();
    context.moveTo(-radius * 0.28, -radius * 0.12);
    context.lineTo(-radius * 0.12, -radius * 0.42);
    context.lineTo(radius * 0.02, -radius * 0.18);
    context.stroke();
  } else if (cell.terrain === "water") {
    [-0.25, 0, 0.25].forEach((offset) => {
      context.beginPath();
      context.moveTo(-radius * 0.55, offset * radius);
      context.quadraticCurveTo(-radius * 0.28, offset * radius - radius * 0.14, 0, offset * radius);
      context.quadraticCurveTo(radius * 0.28, offset * radius + radius * 0.14, radius * 0.55, offset * radius);
      context.stroke();
    });
  } else if (cell.terrain === "hills") {
    context.beginPath();
    context.arc(-radius * 0.2, radius * 0.24, radius * 0.42, Math.PI, Math.PI * 2);
    context.arc(radius * 0.28, radius * 0.24, radius * 0.3, Math.PI, Math.PI * 2);
    context.stroke();
  } else if (cell.terrain === "swamp") {
    [-0.35, 0.2].forEach((offset) => {
      context.beginPath();
      context.moveTo(offset * radius, radius * 0.35);
      context.lineTo(offset * radius, -radius * 0.25);
      context.moveTo(offset * radius, -radius * 0.02);
      context.lineTo(offset * radius - radius * 0.18, -radius * 0.22);
      context.moveTo(offset * radius, radius * 0.08);
      context.lineTo(offset * radius + radius * 0.18, -radius * 0.12);
      context.stroke();
    });
    context.beginPath();
    context.ellipse(0, radius * 0.35, radius * 0.48, radius * 0.12, 0, 0, Math.PI * 2);
    context.stroke();
  } else if (cell.terrain === "desert") {
    context.beginPath();
    context.arc(-radius * 0.12, radius * 0.25, radius * 0.55, Math.PI * 1.08, Math.PI * 1.9);
    context.stroke();
    context.beginPath();
    context.arc(radius * 0.35, -radius * 0.3, radius * 0.1, 0, Math.PI * 2);
    context.fill();
  } else if (cell.terrain === "snow") {
    for (let index = 0; index < 3; index += 1) {
      context.rotate(Math.PI / 3);
      context.beginPath();
      context.moveTo(-radius * 0.42, 0);
      context.lineTo(radius * 0.42, 0);
      context.stroke();
    }
  } else if (cell.terrain === "settlement") {
    context.beginPath();
    context.moveTo(-radius * 0.45, -radius * 0.02);
    context.lineTo(-radius * 0.18, -radius * 0.3);
    context.lineTo(radius * 0.08, -radius * 0.02);
    context.lineTo(radius * 0.08, radius * 0.34);
    context.lineTo(-radius * 0.45, radius * 0.34);
    context.closePath();
    context.stroke();
    context.strokeRect(radius * 0.15, -radius * 0.15, radius * 0.32, radius * 0.49);
  } else {
    [-0.28, 0, 0.28].forEach((offset) => {
      context.beginPath();
      context.moveTo(offset * radius, radius * 0.28);
      context.lineTo(offset * radius - radius * 0.08, -radius * 0.02);
      context.moveTo(offset * radius, radius * 0.12);
      context.lineTo(offset * radius + radius * 0.12, -radius * 0.08);
      context.stroke();
    });
  }
  context.restore();
};

const drawHexTerrain = (
  context: CanvasRenderingContext2D,
  cells: HexCell[],
  gridSize: number,
  viewport: Viewport,
) => {
  cells.forEach((cell) => {
    const path = hexPath(cell, gridSize);
    context.fillStyle = terrainColors[cell.terrain].fill;
    context.fill(path);
    context.strokeStyle = "rgba(51, 48, 38, 0.28)";
    context.lineWidth = 1 / viewport.zoom;
    context.stroke(path);
    drawTerrainSymbol(context, cell, gridSize, viewport);
  });
};

const drawHexGrid = (
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  viewport: Viewport,
  gridSize: number,
  showCoordinates: boolean,
) => {
  const left = -viewport.x / viewport.zoom;
  const top = -viewport.y / viewport.zoom;
  const right = left + width / viewport.zoom;
  const bottom = top + height / viewport.zoom;
  const hexes = visibleHexes(left, top, right, bottom, gridSize);
  const path = new Path2D();
  hexes.forEach((hex) => path.addPath(hexPath(hex, gridSize)));
  context.strokeStyle = "rgba(54, 49, 39, 0.24)";
  context.lineWidth = 1 / viewport.zoom;
  context.stroke(path);

  if (showCoordinates && viewport.zoom >= 0.45) {
    context.fillStyle = "rgba(54, 49, 39, 0.48)";
    context.font = `${8 / viewport.zoom}px ui-monospace, monospace`;
    context.textAlign = "center";
    context.textBaseline = "middle";
    hexes.forEach((hex) => {
      const [x, y] = axialToWorld(hex, gridSize);
      context.fillText(`${hex.q},${hex.r}`, x, y + hexRadius(gridSize) * 0.66);
    });
  }
};

const roundedRect = (
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) => {
  context.beginPath();
  context.roundRect(x, y, width, height, Math.min(radius, width / 2, height / 2));
};


const DM_ONLY_TINT = "#8e3f8b";

/**
 * Draws one member of the door & passage family. Everything is vector-drawn in
 * map style using standard dungeon-key conventions; there are no image assets.
 *
 * The context is already translated to the object's centre and rotated to the
 * wall, so every branch draws around the origin with the wall running along x.
 */
const drawWallObject = (
  context: CanvasRenderingContext2D,
  object: MapObject,
  w: number,
  h: number,
) => {
  const definition = getWallObject(object.wallType);
  const { style } = definition;
  const half = w / 2;
  const ink = definition.dmOnly ? DM_ONLY_TINT : "#4a2e20";

  // Clear the wall behind the opening so the doorway reads as a gap.
  context.fillStyle = "#f8f2e4";
  context.fillRect(-half - 3, -h / 2 - 4, w + 6, h + 8);

  // DM-only features get a dashed outline so they are obvious at a glance.
  if (definition.dmOnly) {
    context.save();
    context.setLineDash([4, 3]);
    context.strokeStyle = DM_ONLY_TINT;
    context.lineWidth = 1.2;
    context.strokeRect(-half - 3, -h / 2 - 4, w + 6, h + 8);
    context.restore();
  }

  context.strokeStyle = ink;
  context.fillStyle = ink;

  // Jambs: the two short stubs that terminate the wall either side of the gap.
  context.lineWidth = 4;
  context.beginPath();
  context.moveTo(-half, -h / 2 - 3);
  context.lineTo(-half, h / 2 + 3);
  context.moveTo(half, -h / 2 - 3);
  context.lineTo(half, h / 2 + 3);
  context.stroke();

  // Threshold line, unless the opening is meant to read as empty.
  if (style.opening !== "full") {
    context.lineWidth = style.leaf === "none" ? 2 : 4;
    context.beginPath();
    context.moveTo(-half, 0);
    context.lineTo(half, 0);
    context.stroke();
  }

  if (style.leaf === "single") {
    context.lineWidth = 1.5;
    context.beginPath();
    context.arc(-half, 0, w, -Math.PI / 2, 0);
    context.stroke();
  } else if (style.leaf === "double") {
    context.lineWidth = 1.5;
    context.beginPath();
    context.arc(-half, 0, half, -Math.PI / 2, 0);
    context.stroke();
    context.beginPath();
    context.arc(half, 0, half, Math.PI, Math.PI * 1.5);
    context.stroke();
  }

  if (style.bars) {
    context.lineWidth = 1.6;
    context.beginPath();
    for (let index = 1; index <= style.bars; index += 1) {
      const x = -half + (w * index) / (style.bars + 1);
      context.moveTo(x, -h / 2 - 2);
      context.lineTo(x, h / 2 + 2);
    }
    context.stroke();
  }

  if (style.wavy) {
    context.lineWidth = 1.6;
    context.beginPath();
    const steps = 12;
    for (let index = 0; index <= steps; index += 1) {
      const x = -half + (w * index) / steps;
      const y = Math.sin((index / steps) * Math.PI * 4) * (h / 2);
      if (index === 0) context.moveTo(x, y);
      else context.lineTo(x, y);
    }
    context.stroke();
  }

  if (style.barred) {
    context.lineWidth = 2.2;
    context.beginPath();
    context.moveTo(-half * 0.8, -h / 2 - 3);
    context.lineTo(half * 0.8, h / 2 + 3);
    context.stroke();
  }

  if (style.lock) {
    context.beginPath();
    context.arc(0, -h / 2 - 5, 2.6, 0, Math.PI * 2);
    context.fill();
    context.lineWidth = 1.6;
    context.beginPath();
    context.moveTo(0, -h / 2 - 5);
    context.lineTo(0, -h / 2 - 1);
    context.stroke();
  }

  if (style.arrow) {
    const tip = h / 2 + 8;
    context.lineWidth = 1.6;
    context.beginPath();
    context.moveTo(0, -h / 2 - 6);
    context.lineTo(0, tip);
    context.stroke();
    context.beginPath();
    context.moveTo(-3.2, tip - 4);
    context.lineTo(0, tip);
    context.lineTo(3.2, tip - 4);
    context.stroke();
  }

  if (style.opening === "window") {
    context.lineWidth = 1.6;
    context.strokeRect(-half * 0.7, -h / 2, w * 0.7, h);
  } else if (style.opening === "slit") {
    context.lineWidth = 2.4;
    context.beginPath();
    context.moveTo(0, -h / 2 - 2);
    context.lineTo(0, h / 2 + 2);
    context.stroke();
  }

  if (style.glyph) {
    context.font = `700 ${Math.max(7, h * 1.5)}px ui-serif, Georgia, serif`;
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(style.glyph, 0, 0);
  }
};


/**
 * Draws one member of the vertical-movement family. Grid-snapped and freely
 * rotatable, so every branch draws around the origin with the object's long
 * axis along x; the caller has already applied translation and rotation.
 */
const drawFloorObject = (
  context: CanvasRenderingContext2D,
  object: MapObject,
  w: number,
  h: number,
) => {
  const definition = getFloorObject(object.floorType);
  const { style } = definition;
  const halfW = w / 2;
  const halfH = h / 2;
  const ink = definition.dmOnly ? DM_ONLY_TINT : "#3a2b21";

  context.strokeStyle = ink;
  context.fillStyle = ink;
  context.lineWidth = 1.6;

  if (definition.dmOnly) {
    context.save();
    context.setLineDash([4, 3]);
    context.strokeRect(-halfW, -halfH, w, h);
    context.restore();
  }

  if (style.steps && !style.wedge && !style.spiral) {
    // Stair treads across the long axis, plus the two stringers.
    context.strokeRect(-halfW, -halfH, w, h);
    context.beginPath();
    for (let index = 1; index < style.steps; index += 1) {
      const x = -halfW + (w * index) / style.steps;
      context.moveTo(x, -halfH);
      context.lineTo(x, halfH);
    }
    context.stroke();
  }

  if (style.spiral) {
    const radius = Math.min(halfW, halfH);
    context.beginPath();
    context.arc(0, 0, radius, 0, Math.PI * 1.75);
    context.stroke();
    context.beginPath();
    const treads = style.steps ?? 8;
    for (let index = 0; index < treads; index += 1) {
      const angle = (index / treads) * Math.PI * 1.75;
      context.moveTo(Math.cos(angle) * radius * 0.28, Math.sin(angle) * radius * 0.28);
      context.lineTo(Math.cos(angle) * radius, Math.sin(angle) * radius);
    }
    context.stroke();
    context.beginPath();
    context.arc(0, 0, radius * 0.22, 0, Math.PI * 2);
    context.fill();
  }

  if (style.rungs) {
    // Two rails down the long axis with rungs between them.
    context.beginPath();
    context.moveTo(-halfW, -halfH);
    context.lineTo(-halfW, halfH);
    context.moveTo(halfW, -halfH);
    context.lineTo(halfW, halfH);
    context.stroke();
    context.beginPath();
    for (let index = 1; index <= style.rungs; index += 1) {
      const y = -halfH + (h * index) / (style.rungs + 1);
      context.moveTo(-halfW, y);
      context.lineTo(halfW, y);
    }
    context.stroke();
  }

  if (style.hatch) {
    context.strokeRect(-halfW, -halfH, w, h);
    context.beginPath();
    context.moveTo(-halfW, -halfH);
    context.lineTo(halfW, halfH);
    context.stroke();
    // Hinge stubs on the leading edge.
    context.beginPath();
    context.moveTo(-halfW, -halfH + h * 0.25);
    context.lineTo(-halfW + w * 0.16, -halfH + h * 0.25);
    context.moveTo(-halfW, halfH - h * 0.25);
    context.lineTo(-halfW + w * 0.16, halfH - h * 0.25);
    context.stroke();
  }

  if (style.pit) {
    const radius = Math.min(halfW, halfH);
    context.beginPath();
    context.ellipse(0, 0, halfW * 0.92, halfH * 0.92, 0, 0, Math.PI * 2);
    context.stroke();
    if (style.pit === "covered") {
      context.save();
      context.setLineDash([3, 3]);
      context.beginPath();
      context.moveTo(-halfW * 0.92, 0);
      context.lineTo(halfW * 0.92, 0);
      context.stroke();
      context.restore();
    } else if (style.pit === "spiked") {
      context.beginPath();
      const teeth = 6;
      for (let index = 0; index < teeth; index += 1) {
        const angle = (index / teeth) * Math.PI * 2;
        const next = ((index + 0.5) / teeth) * Math.PI * 2;
        context.moveTo(Math.cos(angle) * radius * 0.78, Math.sin(angle) * radius * 0.78);
        context.lineTo(Math.cos(next) * radius * 0.2, Math.sin(next) * radius * 0.2);
      }
      context.stroke();
    } else {
      // Open pit: hatched interior so it reads as a hole.
      context.beginPath();
      for (let index = -2; index <= 2; index += 1) {
        const offset = (index * Math.min(w, h)) / 6;
        context.moveTo(offset - halfW * 0.4, -halfH * 0.6);
        context.lineTo(offset + halfW * 0.4, halfH * 0.6);
      }
      context.stroke();
    }
  }

  if (style.rings) {
    for (let index = 0; index < style.rings; index += 1) {
      const scale = 1 - index * (0.7 / style.rings);
      context.beginPath();
      context.ellipse(0, 0, halfW * 0.92 * scale, halfH * 0.92 * scale, 0, 0, Math.PI * 2);
      context.stroke();
    }
  }

  if (style.wedge) {
    // Ramp: a wedge that tapers toward the high end.
    context.beginPath();
    context.moveTo(-halfW, -halfH);
    context.lineTo(halfW, -halfH * 0.25);
    context.lineTo(halfW, halfH * 0.25);
    context.lineTo(-halfW, halfH);
    context.closePath();
    context.stroke();
  }

  if (style.direction) {
    // Direction arrow along the long axis: up points to +x, down to -x.
    const up = style.direction === "up";
    const tipX = up ? halfW * 0.78 : -halfW * 0.78;
    const tailX = -tipX;
    const head = up ? -w * 0.16 : w * 0.16;
    context.lineWidth = 2;
    context.beginPath();
    context.moveTo(tailX, 0);
    context.lineTo(tipX, 0);
    context.stroke();
    context.beginPath();
    context.moveTo(tipX + head, -h * 0.18);
    context.lineTo(tipX, 0);
    context.lineTo(tipX + head, h * 0.18);
    context.stroke();
  }
};


/**
 * Draws one marker or furnishing. Grid-snapped and rotatable, so the caller has
 * already translated to the centre and applied rotation.
 */
const drawMarkerObject = (
  context: CanvasRenderingContext2D,
  object: MapObject,
  w: number,
  h: number,
) => {
  const definition = getMarkerObject(object.markerType);
  const { style } = definition;
  const halfW = w / 2;
  const halfH = h / 2;
  const ink = definition.dmOnly ? DM_ONLY_TINT : "#3a2b21";

  context.strokeStyle = ink;
  context.fillStyle = ink;
  context.lineWidth = 1.6;

  if (definition.dmOnly) {
    context.save();
    context.setLineDash([4, 3]);
    context.strokeRect(-halfW, -halfH, w, h);
    context.restore();
  }

  if (style.column === "round") {
    context.fillStyle = "#cbbba4";
    context.beginPath();
    context.ellipse(0, 0, halfW, halfH, 0, 0, Math.PI * 2);
    context.fill();
    context.strokeStyle = ink;
    context.stroke();
  } else if (style.column === "square") {
    context.fillStyle = "#cbbba4";
    context.fillRect(-halfW, -halfH, w, h);
    context.strokeStyle = ink;
    context.strokeRect(-halfW, -halfH, w, h);
    context.beginPath();
    context.moveTo(-halfW, -halfH);
    context.lineTo(halfW, halfH);
    context.stroke();
  }

  if (style.figure) {
    // Plinth with a simple standing silhouette on top.
    context.fillStyle = "#bfae97";
    context.fillRect(-halfW, halfH - h * 0.22, w, h * 0.22);
    context.strokeStyle = ink;
    context.strokeRect(-halfW, halfH - h * 0.22, w, h * 0.22);
    context.beginPath();
    context.arc(0, -halfH + h * 0.2, Math.min(w, h) * 0.16, 0, Math.PI * 2);
    context.stroke();
    context.beginPath();
    context.moveTo(0, -halfH + h * 0.34);
    context.lineTo(0, halfH - h * 0.22);
    context.moveTo(-w * 0.22, -halfH + h * 0.46);
    context.lineTo(w * 0.22, -halfH + h * 0.46);
    context.stroke();
  }

  if (style.block) {
    context.fillStyle = style.block === "dais" ? "#c3b39c" : "#bda98e";
    context.fillRect(-halfW, -halfH, w, h);
    context.strokeStyle = ink;
    context.strokeRect(-halfW, -halfH, w, h);
    if (style.block === "dais") {
      // Concentric step rings.
      context.strokeRect(-halfW * 0.68, -halfH * 0.68, w * 0.68, h * 0.68);
      context.strokeRect(-halfW * 0.36, -halfH * 0.36, w * 0.36, h * 0.36);
    } else if (style.block === "altar") {
      context.beginPath();
      context.moveTo(-halfW * 0.7, -halfH * 0.45);
      context.lineTo(halfW * 0.7, -halfH * 0.45);
      context.moveTo(-halfW * 0.7, halfH * 0.45);
      context.lineTo(halfW * 0.7, halfH * 0.45);
      context.stroke();
    } else {
      // Throne: a high back plus two arms.
      context.beginPath();
      context.moveTo(-halfW, -halfH);
      context.lineTo(-halfW, halfH);
      context.stroke();
      context.beginPath();
      context.moveTo(-halfW * 0.4, -halfH);
      context.lineTo(halfW, -halfH);
      context.moveTo(-halfW * 0.4, halfH);
      context.lineTo(halfW, halfH);
      context.stroke();
    }
  }

  if (style.flame) {
    context.fillStyle = "#c8703a";
    context.beginPath();
    context.ellipse(0, 0, halfW, halfH, 0, 0, Math.PI * 2);
    context.fill();
    context.strokeStyle = ink;
    context.stroke();
    context.beginPath();
    context.moveTo(0, halfH * 0.4);
    context.quadraticCurveTo(halfW * 0.6, -halfH * 0.2, 0, -halfH * 0.75);
    context.quadraticCurveTo(-halfW * 0.6, -halfH * 0.2, 0, halfH * 0.4);
    context.stroke();
  }

  if (style.chest) {
    const band = style.chest === "gold" ? "#d5a72f" : style.chest === "iron" ? "#7f8789" : "#9b633c";
    context.fillStyle = band;
    context.fillRect(-halfW, -halfH, w, h);
    context.strokeStyle = ink;
    context.strokeRect(-halfW, -halfH, w, h);
    context.beginPath();
    context.moveTo(-halfW, -halfH * 0.15);
    context.lineTo(halfW, -halfH * 0.15);
    context.stroke();
    context.beginPath();
    context.arc(0, halfH * 0.2, Math.min(w, h) * 0.1, 0, Math.PI * 2);
    context.fill();
  }

  if (style.glyph) {
    context.beginPath();
    context.ellipse(0, 0, halfW, halfH, 0, 0, Math.PI * 2);
    context.stroke();
    context.font = `700 ${Math.max(8, Math.min(w, h) * 0.72)}px ui-serif, Georgia, serif`;
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(style.glyph, 0, 0);
  }
};

/**
 * Terrain patches: painted regions clipped to the floor, drawn above the floor
 * fill and below the grid, walls and objects. Clipping stops a patch bleeding
 * outside the room it was painted into.
 */
const drawTerrainPatches = (
  context: CanvasRenderingContext2D,
  patches: TerrainPatch[],
  geometry: MultiPolygon,
  gridSize: number,
) => {
  if (patches.length === 0) return;
  context.save();
  if (geometry.length > 0) {
    context.clip(geometryPath(geometry), "evenodd");
  }

  patches.forEach((patch) => {
    const definition = getTerrainPatch(patch.type);
    const path = polygonPath(patch.polygon);
    context.save();
    context.fillStyle = definition.fill;
    context.fill(path, "evenodd");
    context.clip(path, "evenodd");

    const bounds = ringBounds(patch.polygon);
    const step = gridSize / definition.density;
    context.strokeStyle = definition.ink;
    context.fillStyle = definition.ink;
    context.lineWidth = 1;

    if (definition.pattern === "hatch") {
      context.beginPath();
      const span = bounds.maxX - bounds.minX + bounds.maxY - bounds.minY;
      for (let offset = 0; offset <= span; offset += step) {
        context.moveTo(bounds.minX + offset, bounds.minY);
        context.lineTo(bounds.minX + offset - (bounds.maxY - bounds.minY), bounds.maxY);
      }
      context.stroke();
    } else if (definition.pattern === "wave") {
      context.beginPath();
      for (let y = bounds.minY; y <= bounds.maxY; y += step) {
        context.moveTo(bounds.minX, y);
        for (let x = bounds.minX; x <= bounds.maxX; x += step / 2) {
          context.lineTo(x, y + Math.sin((x / step) * Math.PI) * (step * 0.18));
        }
      }
      context.stroke();
    } else {
      // Stipple: deterministic dots, so a redraw or a print looks identical.
      context.beginPath();
      let seed = 1;
      for (let y = bounds.minY; y <= bounds.maxY; y += step) {
        for (let x = bounds.minX; x <= bounds.maxX; x += step) {
          seed = (seed * 1103515245 + 12345) % 2147483648;
          const jitterX = ((seed / 2147483648) - 0.5) * step * 0.7;
          seed = (seed * 1103515245 + 12345) % 2147483648;
          const jitterY = ((seed / 2147483648) - 0.5) * step * 0.7;
          context.moveTo(x + jitterX, y + jitterY);
          context.arc(x + jitterX, y + jitterY, step * 0.09, 0, Math.PI * 2);
        }
      }
      context.fill();
    }
    context.restore();
  });

  context.restore();
};

/** Bounding box of a polygon's rings. */
const ringBounds = (polygon: Polygon) => {
  let minX = Number.POSITIVE_INFINITY, minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY, maxY = Number.NEGATIVE_INFINITY;
  polygon.forEach((ring) => ring.forEach(([x, y]) => {
    minX = Math.min(minX, x); minY = Math.min(minY, y);
    maxX = Math.max(maxX, x); maxY = Math.max(maxY, y);
  }));
  return Number.isFinite(minX) ? { minX, minY, maxX, maxY } : { minX: 0, minY: 0, maxX: 0, maxY: 0 };
};

const drawStamp = (context: CanvasRenderingContext2D, object: MapObject) => {
  const { width: w, height: h } = object;
  context.save();
  context.translate(object.x + w / 2, object.y + h / 2);
  context.rotate((object.rotation * Math.PI) / 180);
  context.globalAlpha = object.opacity ?? 1;
  context.lineCap = "round";
  context.lineJoin = "round";
  context.lineWidth = 2;
  context.strokeStyle = "#3a2b21";
  context.fillStyle = "#a8784f";

  if (object.assetId === "text_label") {
    const fontSize = Math.min(object.fontSize ?? 24, h * 0.72);
    context.fillStyle = object.backgroundColor ?? "#f8f2e4";
    context.globalAlpha = (object.opacity ?? 1) * 0.9;
    roundedRect(context, -w / 2, -h / 2, w, h, Math.min(8, h * 0.25));
    context.fill();
    context.globalAlpha = object.opacity ?? 1;
    context.strokeStyle = "rgba(74, 59, 47, 0.18)";
    context.lineWidth = 1;
    context.stroke();
    context.fillStyle = object.color ?? "#2d241d";
    context.font = `600 ${fontSize}px ui-sans-serif, system-ui, sans-serif`;
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(object.text ?? t("doc.labelFallback"), 0, 0, Math.max(1, w - 14));
  } else if (object.assetId === "room_number") {
    const radius = Math.min(w, h) / 2;
    context.fillStyle = object.backgroundColor ?? "#f8f2e4";
    context.strokeStyle = object.color ?? "#2d241d";
    context.lineWidth = Math.max(1.5, radius * 0.09);
    context.beginPath();
    context.arc(0, 0, radius - context.lineWidth, 0, Math.PI * 2);
    context.fill();
    context.stroke();
    context.fillStyle = object.color ?? "#2d241d";
    context.font = `700 ${Math.min(object.fontSize ?? 24, radius * 1.05)}px ui-serif, Georgia, serif`;
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(object.text ?? "1", 0, 1, radius * 1.45);
  } else if (["token_player", "token_enemy", "token_npc"].includes(object.assetId)) {
    const radius = Math.min(w, h) / 2;
    const player = object.assetId === "token_player";
    const enemy = object.assetId === "token_enemy";
    context.fillStyle = player ? "#dfe9f4" : enemy ? "#f1d6d1" : "#e4dfc8";
    context.strokeStyle = player ? "#315f86" : enemy ? "#9d302b" : "#6b6035";
    context.lineWidth = Math.max(2, radius * 0.12);
    context.beginPath();
    context.arc(0, 0, radius - context.lineWidth / 2, 0, Math.PI * 2);
    context.fill();
    context.stroke();
    context.fillStyle = context.strokeStyle;
    context.font = `800 ${radius * 0.95}px ui-sans-serif, system-ui, sans-serif`;
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(player ? t("glyph.tokenPlayer") : enemy ? t("glyph.tokenEnemy") : t("glyph.tokenNpc"), 0, 1);
  } else if (object.assetId === "door") {
    drawWallObject(context, object, w, h);
  } else if (object.assetId === "portal") {
    context.fillStyle = "rgba(87, 52, 126, 0.18)";
    context.strokeStyle = "#4d2d69";
    context.lineWidth = 3;
    context.beginPath();
    context.ellipse(0, 0, w / 2, h / 2, 0, 0, Math.PI * 2);
    context.fill();
    context.stroke();
    context.setLineDash([5, 4]);
    context.lineWidth = 1.5;
    context.beginPath();
    context.ellipse(0, 0, w * 0.32, h * 0.32, 0, 0, Math.PI * 2);
    context.stroke();
  } else if (object.assetId === "table") {
    roundedRect(context, -w / 2, -h / 2, w, h, Math.min(w, h) * 0.18);
    context.fill();
    context.stroke();
    context.fillStyle = "#513b2b";
    TABLE_LEG_OFFSETS.forEach(([x, y]) => {
      context.beginPath();
      context.arc(x * w, y * h, 2.5, 0, Math.PI * 2);
      context.fill();
    });
  } else if (object.assetId === "crate") {
    context.fillRect(-w / 2, -h / 2, w, h);
    context.strokeRect(-w / 2, -h / 2, w, h);
    context.beginPath();
    context.moveTo(-w / 2, -h / 2);
    context.lineTo(w / 2, h / 2);
    context.moveTo(w / 2, -h / 2);
    context.lineTo(-w / 2, h / 2);
    context.stroke();
  } else if (object.assetId === "bed") {
    roundedRect(context, -w / 2, -h / 2, w, h, 5);
    context.fillStyle = "#8f6851";
    context.fill();
    context.stroke();
    context.fillStyle = "#ded2bb";
    roundedRect(context, -w * 0.38, -h * 0.4, w * 0.76, h * 0.22, 4);
    context.fill();
    context.stroke();
  } else if (object.assetId === "rug") {
    context.fillStyle = "rgba(145, 48, 43, 0.62)";
    roundedRect(context, -w / 2, -h / 2, w, h, 7);
    context.fill();
    context.stroke();
    context.strokeStyle = "rgba(246, 210, 149, 0.75)";
    context.setLineDash([5, 4]);
    context.strokeRect(-w * 0.4, -h * 0.32, w * 0.8, h * 0.64);
  } else if (object.assetId === "stairs") {
    drawFloorObject(context, object, w, h);
  } else if (object.assetId === "marker") {
    drawMarkerObject(context, object, w, h);
  }

  context.restore();
};

const drawSelection = (
  context: CanvasRenderingContext2D,
  object: MapObject,
  viewport: Viewport,
) => {
  const handle = 9 / viewport.zoom;
  const rotationHandle = 8 / viewport.zoom;
  const rotationOffset = 32 / viewport.zoom;
  context.save();
  context.translate(object.x + object.width / 2, object.y + object.height / 2);
  context.rotate((object.rotation * Math.PI) / 180);
  context.strokeStyle = object.locked ? "#70675e" : "#b4362f";
  context.fillStyle = "#f7eee0";
  context.lineWidth = 1.5 / viewport.zoom;
  context.setLineDash([5 / viewport.zoom, 4 / viewport.zoom]);
  context.strokeRect(-object.width / 2, -object.height / 2, object.width, object.height);
  context.setLineDash([]);
  if (!object.locked) {
    CORNER_SIGNS.forEach(([x, y]) => {
      context.fillRect(
        (x * object.width) / 2 - handle / 2,
        (y * object.height) / 2 - handle / 2,
        handle,
        handle,
      );
      context.strokeRect(
        (x * object.width) / 2 - handle / 2,
        (y * object.height) / 2 - handle / 2,
        handle,
        handle,
      );
    });

    const objectTop = -object.height / 2;
    const rotateY = objectTop - rotationOffset;
    context.beginPath();
    context.moveTo(0, objectTop);
    context.lineTo(0, rotateY + rotationHandle);
    context.stroke();
    context.fillStyle = "#b4362f";
    context.beginPath();
    context.arc(0, rotateY, rotationHandle, 0, Math.PI * 2);
    context.fill();
    context.fillStyle = "#fff8ed";
    context.beginPath();
    context.arc(0, rotateY, 2.1 / viewport.zoom, 0, Math.PI * 2);
    context.fill();
  }
  context.restore();
};

export const mapContentBounds = (
  geometry: MultiPolygon,
  objects: MapObject[],
  hexCells: HexCell[] = [],
  gridSize = 50,
  terrainPatches: TerrainPatch[] = [],
) => {
  const bounds = geometryBounds(geometry);
  if (geometry.length === 0 && (objects.length > 0 || hexCells.length > 0)) {
    bounds.minX = Number.POSITIVE_INFINITY;
    bounds.minY = Number.POSITIVE_INFINITY;
    bounds.maxX = Number.NEGATIVE_INFINITY;
    bounds.maxY = Number.NEGATIVE_INFINITY;
  }
  objects.forEach((object) => {
    const radius = Math.hypot(object.width, object.height) / 2;
    const centerX = object.x + object.width / 2;
    const centerY = object.y + object.height / 2;
    bounds.minX = Math.min(bounds.minX, centerX - radius);
    bounds.minY = Math.min(bounds.minY, centerY - radius);
    bounds.maxX = Math.max(bounds.maxX, centerX + radius);
    bounds.maxY = Math.max(bounds.maxY, centerY + radius);
  });
  const hexBounds = terrainBounds(hexCells, gridSize);
  if (hexBounds) {
    bounds.minX = Math.min(bounds.minX, hexBounds.minX);
    bounds.minY = Math.min(bounds.minY, hexBounds.minY);
    bounds.maxX = Math.max(bounds.maxX, hexBounds.maxX);
    bounds.maxY = Math.max(bounds.maxY, hexBounds.maxY);
  }
  // Patches count toward the map's extent, or fit-to-map and PDF export would
  // crop a region the user can plainly see.
  terrainPatches.forEach((patch) => {
    const patchBounds = ringBounds(patch.polygon);
    bounds.minX = Math.min(bounds.minX, patchBounds.minX);
    bounds.minY = Math.min(bounds.minY, patchBounds.minY);
    bounds.maxX = Math.max(bounds.maxX, patchBounds.maxX);
    bounds.maxY = Math.max(bounds.maxY, patchBounds.maxY);
  });
  return Number.isFinite(bounds.minX)
    ? bounds
    : { minX: 0, minY: 0, maxX: 500, maxY: 500 };
};

export const renderMapScene = ({
  context,
  width,
  height,
  deviceScale,
  viewport,
  meta,
  geometry,
  hexCells = [],
  objects = [],
  terrainPatches = [],
  selectedObjectId = null,
  preview,
  draftPolygon,
  measurement,
  hexBrushPreview,
  showVignette = true,
}: RenderOptions) => {
  const sceneGeometry = floorForMode(geometry, meta.gridType);
  const sceneHexCells = hexesForMode(hexCells, meta.gridType);
  const sceneObjects = objectsForMode(objects, meta.gridType);
  // Patches belong to the square canvas only, like floor geometry.
  const scenePatches = meta.gridType === "square" ? terrainPatches : [];
  context.setTransform(deviceScale, 0, 0, deviceScale, 0, 0);
  context.clearRect(0, 0, width, height);

  context.fillStyle = meta.backgroundColor;
  context.fillRect(0, 0, width, height);
  const paperLight = context.createLinearGradient(0, 0, width, height);
  paperLight.addColorStop(0, "rgba(255, 255, 255, 0.12)");
  paperLight.addColorStop(0.52, "rgba(255, 255, 255, 0.02)");
  paperLight.addColorStop(1, "rgba(48, 35, 24, 0.08)");
  context.fillStyle = paperLight;
  context.fillRect(0, 0, width, height);

  context.save();
  context.setTransform(
    deviceScale * viewport.zoom, 0, 0, deviceScale * viewport.zoom,
    deviceScale * viewport.x, deviceScale * viewport.y,
  );

  if (meta.gridType === "hex" && sceneHexCells.length > 0) {
    drawHexTerrain(context, sceneHexCells, meta.gridSizeWorld, viewport);
  }

  if (sceneGeometry.length > 0 && meta.showHatching !== false) {
    const path = geometryPath(sceneGeometry);
    context.save();
    context.strokeStyle = makeHatchPattern(context, meta.hatchColor);
    context.lineJoin = "round";
    context.lineCap = "round";
    context.lineWidth = Math.max(16, meta.gridSizeWorld * 0.68);
    context.stroke(path);
    context.restore();
  }

  if (sceneGeometry.length > 0) {
    const path = geometryPath(sceneGeometry);
    context.save();
    context.shadowColor = "rgba(43, 31, 20, 0.2)";
    context.shadowBlur = 12;
    context.shadowOffsetY = 5;
    context.fillStyle = meta.floorColor;
    context.fill(path, "evenodd");
    context.restore();
  }

  // Terrain patches sit directly on the floor: above the fill, below the grid,
  // walls and objects. See DECISIONS.md D-021 for why this is the insertion point.
  if (scenePatches.length > 0) {
    drawTerrainPatches(context, scenePatches, sceneGeometry, meta.gridSizeWorld);
  }

  if (meta.showGrid !== false) {
    if (meta.gridType === "hex") {
      drawHexGrid(
        context,
        width,
        height,
        viewport,
        meta.gridSizeWorld,
        meta.showHexCoordinates === true,
      );
    } else {
      drawSquareGrid(context, width, height, viewport, meta.gridSizeWorld);
    }
  }

  if (meta.gridType === "hex" && hexBrushPreview) {
    const path = hexPath(hexBrushPreview, meta.gridSizeWorld);
    context.save();
    context.fillStyle = hexBrushPreview.mode === "erase"
      ? "rgba(164, 47, 42, 0.2)"
      : "rgba(247, 238, 224, 0.28)";
    context.strokeStyle = hexBrushPreview.mode === "erase" ? "#b4362f" : "#f7eee0";
    context.lineWidth = 2.5 / viewport.zoom;
    context.setLineDash([6 / viewport.zoom, 4 / viewport.zoom]);
    context.fill(path);
    context.stroke(path);
    context.restore();
  }

  if (sceneGeometry.length > 0) {
    const path = geometryPath(sceneGeometry);
    context.lineJoin = "round";
    context.lineCap = "round";
    context.strokeStyle = meta.wallColor;
    context.lineWidth = meta.wallWidth ?? 4;
    context.stroke(path);
    context.strokeStyle = "rgba(255, 255, 255, 0.42)";
    context.lineWidth = 1.1;
    context.stroke(path);
  }

  [...sceneObjects].sort((a, b) => a.zIndex - b.zIndex).forEach((object) => drawStamp(context, object));
  const selected = sceneObjects.find((object) => object.id === selectedObjectId);
  if (selected) drawSelection(context, selected, viewport);

  if (preview) {
    const path = polygonPath(preview.polygon);
    const isAdd = preview.mode === "add";
    context.save();
    context.fillStyle = isAdd ? "rgba(171, 44, 44, 0.18)" : "rgba(30, 26, 22, 0.28)";
    context.strokeStyle = isAdd ? "#a52b2b" : "#342b24";
    context.lineWidth = 2 / viewport.zoom;
    context.setLineDash([10 / viewport.zoom, 7 / viewport.zoom]);
    context.fill(path, "evenodd");
    context.stroke(path);
    context.restore();
  }

  if (draftPolygon && draftPolygon.points.length > 0) {
    const points = draftPolygon.cursor
      ? [...draftPolygon.points, draftPolygon.cursor]
      : draftPolygon.points;
    context.save();
    context.strokeStyle = "#a52b2b";
    context.fillStyle = "#f7eee0";
    context.lineWidth = 2 / viewport.zoom;
    context.setLineDash([8 / viewport.zoom, 5 / viewport.zoom]);
    context.beginPath();
    points.forEach(([x, y], index) => index === 0 ? context.moveTo(x, y) : context.lineTo(x, y));
    context.stroke();
    context.setLineDash([]);
    draftPolygon.points.forEach(([x, y], index) => {
      context.beginPath();
      context.arc(x, y, (index === 0 ? 6 : 4) / viewport.zoom, 0, Math.PI * 2);
      context.fill();
      context.stroke();
    });
    context.restore();
  }

  if (measurement) {
    const { start, end, label } = measurement;
    const middleX = (start[0] + end[0]) / 2;
    const middleY = (start[1] + end[1]) / 2;
    context.save();
    context.strokeStyle = "#a72f29";
    context.fillStyle = "#a72f29";
    context.lineWidth = 2.4 / viewport.zoom;
    context.setLineDash([8 / viewport.zoom, 5 / viewport.zoom]);
    context.beginPath();
    context.moveTo(start[0], start[1]);
    context.lineTo(end[0], end[1]);
    context.stroke();
    context.setLineDash([]);
    [start, end].forEach(([x, y]) => {
      context.beginPath();
      context.arc(x, y, 5 / viewport.zoom, 0, Math.PI * 2);
      context.fill();
    });
    const fontSize = 11 / viewport.zoom;
    context.font = `700 ${fontSize}px ui-sans-serif, system-ui, sans-serif`;
    const labelWidth = context.measureText(label).width + 18 / viewport.zoom;
    const labelHeight = 25 / viewport.zoom;
    context.fillStyle = "rgba(32, 30, 26, 0.94)";
    roundedRect(
      context,
      middleX - labelWidth / 2,
      middleY - labelHeight / 2,
      labelWidth,
      labelHeight,
      7 / viewport.zoom,
    );
    context.fill();
    context.fillStyle = "#fff6e9";
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(label, middleX, middleY + 0.5 / viewport.zoom);
    context.restore();
  }

  context.restore();

  if (showVignette) {
    const vignette = context.createRadialGradient(
      width / 2, height / 2, Math.min(width, height) * 0.2,
      width / 2, height / 2, Math.max(width, height) * 0.72,
    );
    vignette.addColorStop(0, "rgba(44, 34, 24, 0)");
    vignette.addColorStop(1, "rgba(44, 34, 24, 0.1)");
    context.fillStyle = vignette;
    context.fillRect(0, 0, width, height);
  }
};
